import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { REACT_APP_API_URL, AUTH_USE_COOKIES } from '../config/constants';
import { clearTokens, getToken } from './auth/tokenStorage';
import { fetchCsrfToken, getCsrfToken, readXsrfCookie, refreshSession } from './auth/session';
import { maybeRedirectForBillingError } from './auth/billingInterceptors';
import { maybeNotifyFeatureNotAvailable } from './auth/featureUpgradeInterceptor';
import { triggerSessionTeardown } from './auth/sessionTeardown';
import { useActiveCustomerStore } from '../store/useActiveCustomerStore';
import { isServerOrNetworkError } from '../utils/serverError';

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRefresh?: boolean;
    _retry?: boolean;
  }
}

const api = axios.create({
  baseURL: `${REACT_APP_API_URL}`,
  withCredentials: AUTH_USE_COOKIES,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const attachCsrfHeader = (config: InternalAxiosRequestConfig): void => {
  const method = (config.method ?? 'get').toLowerCase();
  const isMutation = ['post', 'put', 'patch', 'delete'].includes(method);
  if (!isMutation) return;

  const bearerToken = getToken();
  if (bearerToken) return;

  const csrf = getCsrfToken() ?? readXsrfCookie();
  if (!csrf) return;

  config.headers = config.headers ?? {};
  config.headers['X-CSRF-Token'] = csrf;
};

api.interceptors.request.use((config) => {
  const newConfig = { ...config };
  const token = getToken();
  if (token) {
    newConfig.headers = newConfig.headers ?? {};
    newConfig.headers.Authorization = `Bearer ${token}`;
  }

  const activeCustomerId = useActiveCustomerStore.getState().activeCustomerId;
  if (activeCustomerId) {
    newConfig.headers = newConfig.headers ?? {};
    newConfig.headers['X-Customer-Id'] = activeCustomerId;
  }

  if (AUTH_USE_COOKIES) {
    attachCsrfHeader(newConfig);
  }

  return newConfig;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { skipAuthRefresh?: boolean; _retry?: boolean })
      | undefined;

    if (
      error.response?.status === 403 &&
      AUTH_USE_COOKIES &&
      originalRequest &&
      !originalRequest._retry
    ) {
      const body = error.response.data as { message?: string } | undefined;
      const isCsrfError =
        typeof body?.message === 'string' && body.message.toLowerCase().includes('csrf');

      if (isCsrfError) {
        originalRequest._retry = true;
        try {
          await fetchCsrfToken();
          return api(originalRequest);
        } catch (csrfError) {
          return Promise.reject(csrfError);
        }
      }
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest.skipAuthRefresh) {
      if (originalRequest._retry) {
        clearTokens();
        triggerSessionTeardown();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise<string | null>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (token && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const data = await refreshSession();
        const newToken = data.accessToken ?? null;
        processQueue(null, newToken);
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // 502/ERR_NETWORK: token pode ainda ser válido — não forçar logout
        if (!isServerOrNetworkError(refreshError)) {
          clearTokens();
          triggerSessionTeardown();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (maybeRedirectForBillingError(error)) {
      return new Promise(() => undefined);
    }

    maybeNotifyFeatureNotAvailable(error);

    return Promise.reject(error);
  }
);

export default api;
